import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import recoverMeLogo from "@assets/forgerestore-logo_1779932627674.png";

/**
 * One-time display-name prompt for existing accounts that predate the
 * "everyone must have a display name" requirement. New users set their name
 * during onboarding (RoleSelection); this gate catches anyone whose
 * firstName is still empty. The saved name is how the person appears to
 * others throughout the app.
 */
export default function DisplayNamePrompt() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const { toast } = useToast();

  const saveMutation = useMutation({
    mutationFn: async (data: { firstName: string; lastName?: string }) => {
      await apiRequest("PATCH", "/api/user/profile", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save your name. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    if (!firstName.trim()) return;
    saveMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim() || undefined,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted p-8">
      <Card className="max-w-md w-full p-8 space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img src={recoverMeLogo} alt="ForgeRestore" className="w-12 h-12 object-contain" />
            <h1 className="text-2xl font-semibold text-[#0A4D68] dark:text-[#FFB84C]">
              What should we call you?
            </h1>
          </div>
          <p className="text-muted-foreground text-sm">
            Please choose a name so others can recognize you in your home group and sponsorship family.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="display-first-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                id="display-first-name"
                placeholder="First name or nickname"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="input-display-first-name"
              />
              <Input
                id="display-last-name"
                placeholder="Last initial (optional)"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                data-testid="input-display-last-name"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              This is how you'll appear to others. You don't have to use your real name — pick any name you like, as long as it isn't juvenile or offensive.
            </p>
          </div>

          <Button
            className="w-full"
            size="lg"
            onClick={handleSave}
            disabled={!firstName.trim() || saveMutation.isPending}
            data-testid="button-save-display-name"
          >
            {saveMutation.isPending ? (
              "Saving..."
            ) : (
              <>
                <UserCircle className="w-4 h-4 mr-2" />
                Continue
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
